import React, { useState } from 'react';
import { type UserProfile, type RegistrationRequest } from '../../types';
import { lhrApiService } from '../../services/lhrApiService';
import { useToast } from '../ui/Toast';
import { HumanRisksLogo } from '../ui/HumanRisksLogo';

interface LoginScreenProps {
  onLoginSuccess: (user: UserProfile) => void | Promise<void>;
}

const EMPTY_REGISTRATION: RegistrationRequest = {
  email: '',
  firstName: '',
  lastName: '',
  department: '',
  justification: '',
  timestamp: '',
};

export const LoginScreen: React.FC<LoginScreenProps> = ({ onLoginSuccess }) => {
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);

  const [email, setEmail] = useState('');
  const [regForm, setRegForm] = useState<RegistrationRequest>(EMPTY_REGISTRATION);

  const handleEmailLogin = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!email) {
      showToast('Please enter your work email.', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const user = await lhrApiService.authenticate(email);

      if (user) {
        onLoginSuccess(user);
      } else {
        if (email.includes('@lombard.co.za') || email.includes('@humanrisks.co.za')) {
          showToast('Account not found. Please request access.', 'error');
          setRegForm((prev) => ({ ...prev, email }));
          setShowRegistration(true);
        } else {
          showToast('Email not recognised in dev directory.', 'error');
        }
      }
    } catch {
      showToast('Authentication Service Unavailable', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegistrationSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await lhrApiService.requestAccess({ ...regForm, timestamp: new Date().toISOString() });
      showToast('Access Request Sent to Governance Team', 'success');
      setShowRegistration(false);
    } catch {
      showToast('Failed to submit request', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (showRegistration) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hr-grey p-6 font-sans">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-fade-in-up border border-slate-100">
          <div className="bg-hr-indigo p-8 text-white relative overflow-hidden">
            <div className="relative z-10">
              <HumanRisksLogo variant="white" className="h-6 mb-4" />
              <h2 className="text-lg font-bold tracking-tight">Request Platform Access</h2>
              <p className="text-slate-300 text-micro mt-1">CORTEX by Human Risks</p>
            </div>
          </div>
          <form onSubmit={handleRegistrationSubmit} className="p-8 space-y-5">
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r text-amber-900 text-micro leading-relaxed">
              No active profile found for <strong>{regForm.email}</strong>. Please complete your
              details below for governance approval.
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="reg-first-name"
                  className="block text-micro font-bold text-slate-500 uppercase tracking-wider mb-1.5"
                >
                  First Name
                </label>
                <input
                  id="reg-first-name"
                  required
                  className="w-full p-2.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-hr-indigo focus:border-hr-indigo outline-none transition-all"
                  value={regForm.firstName}
                  onChange={(e) => setRegForm({ ...regForm, firstName: e.target.value })}
                  placeholder="e.g. Sarah"
                />
              </div>
              <div>
                <label
                  htmlFor="reg-last-name"
                  className="block text-micro font-bold text-slate-500 uppercase tracking-wider mb-1.5"
                >
                  Last Name
                </label>
                <input
                  id="reg-last-name"
                  required
                  className="w-full p-2.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-hr-indigo focus:border-hr-indigo outline-none transition-all"
                  value={regForm.lastName}
                  onChange={(e) => setRegForm({ ...regForm, lastName: e.target.value })}
                  placeholder="e.g. Jenkins"
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="reg-department"
                className="block text-micro font-bold text-slate-500 uppercase tracking-wider mb-1.5"
              >
                Department / Branch
              </label>
              <input
                id="reg-department"
                required
                className="w-full p-2.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-hr-indigo focus:border-hr-indigo outline-none transition-all"
                value={regForm.department}
                onChange={(e) => setRegForm({ ...regForm, department: e.target.value })}
                placeholder="e.g. Commercial Underwriting - JHB"
              />
            </div>
            <div>
              <label
                htmlFor="reg-justification"
                className="block text-micro font-bold text-slate-500 uppercase tracking-wider mb-1.5"
              >
                Business Justification
              </label>
              <textarea
                id="reg-justification"
                required
                className="w-full p-2.5 border border-slate-300 rounded-lg h-24 text-xs focus:ring-2 focus:ring-hr-indigo focus:border-hr-indigo outline-none transition-all resize-none"
                placeholder="Reason for access request..."
                value={regForm.justification}
                onChange={(e) => setRegForm({ ...regForm, justification: e.target.value })}
              />
            </div>
            <div className="flex gap-4 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowRegistration(false)}
                className="px-6 py-2.5 text-slate-500 font-bold text-micro uppercase tracking-wide hover:text-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 bg-hr-indigo text-white py-2.5 rounded-lg font-bold text-micro uppercase tracking-widest shadow-lg hover:bg-slate-800 transition-colors flex justify-center items-center gap-2"
              >
                {isLoading ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-hr-grey font-sans selection:bg-hr-pink selection:text-white">
      <div className="hidden lg:flex w-1/2 bg-hr-indigo relative overflow-hidden flex-col justify-between p-16">
        <div className="relative z-10">
          <HumanRisksLogo variant="white" className="h-10 mb-12" />
          <div className="space-y-6">
            <h1 className="text-5xl font-black text-white leading-[0.9] tracking-tight">
              CORTEX
              <span className="text-lg font-light text-hr-pink tracking-tight block mt-2">
                by Human Risks
              </span>
            </h1>
            <p className="text-slate-300 text-sm max-w-md font-light leading-relaxed">
              The brain behind the risk.
            </p>
          </div>
        </div>

        <div className="relative z-10 bg-white/5 border border-white/10 p-6 rounded-2xl max-w-xs mt-12 shadow-2xl">
          <div className="text-micro font-bold text-hr-pink uppercase tracking-widest mb-2">
            System Status
          </div>
          <div className="flex items-center gap-3">
            <span className="w-2.5 h-2.5 bg-hr-lime rounded-full" />
            <span className="text-white font-mono text-xs tracking-wide">ENGINE v1.0 ONLINE</span>
          </div>
          <div className="mt-4 pt-4 border-t border-white/10 text-micro text-slate-400">
            Authorized use only. Mock authentication is enabled in this development build — real
            OIDC swap-in is scheduled when the Identity Provider is provisioned.
          </div>
        </div>

        <div className="z-10 text-micro text-slate-500 font-bold uppercase tracking-widest mt-auto">
          © {new Date().getFullYear()} CORTEX by Human Risks (a specialist division of Lombard
          Insurance Company Limited)
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16 bg-white relative">
        <div className="w-full max-w-[400px] space-y-8">
          <div className="text-center lg:text-left">
            <h2 className="text-2xl font-black text-hr-indigo tracking-tight">Welcome Back</h2>
            <p className="text-slate-500 mt-2 text-xs font-medium">
              Please authenticate to access the underwriting console.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded text-micro font-bold uppercase tracking-widest text-amber-700">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Mock mode — dev only
            </div>
          </div>

          <form onSubmit={handleEmailLogin} className="space-y-5">
            <div className="space-y-1">
              <label
                htmlFor="login-email"
                className="block text-micro font-bold text-slate-700 ml-1"
              >
                Work Email
              </label>
              <input
                id="login-email"
                type="email"
                required
                autoComplete="email"
                className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-900 focus:ring-2 focus:ring-hr-pink focus:border-hr-pink outline-none transition-colors placeholder:text-slate-400"
                placeholder="name@lombard.co.za"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <p className="text-micro text-slate-400 mt-1 ml-1">
                Try <code className="font-mono">admin@lombard.co.za</code> or{' '}
                <code className="font-mono">sarah.j@aon.co.za</code> in mock mode.
              </p>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-hr-pink hover:bg-hr-pink-hover text-white font-bold py-3.5 px-4 rounded-xl shadow-lg transition-colors text-xs uppercase tracking-wider flex justify-center items-center gap-2"
            >
              {isLoading ? 'Verifying…' : 'Log In'}
            </button>
          </form>

          <div className="pt-6">
            <p className="text-micro text-slate-400 text-center font-medium">
              New to the platform?{' '}
              <button
                type="button"
                onClick={() => {
                  setShowRegistration(true);
                  setRegForm({ ...regForm, email });
                }}
                className="font-bold text-hr-indigo hover:text-hr-pink transition-colors underline decoration-2 decoration-transparent hover:decoration-current underline-offset-4"
              >
                Request Access
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
